import type { HttpTransport } from '@mangovoice/api-client'
import type { PaginatedApiResult, PhoneNumber } from '@mangovoice/types'

import { stringifyParams } from './utils'

export interface PhoneNumberListParams {
  pbx_id?: number
  limit?: number
  offset?: number
  search?: string
  ordering?: string
  status?: string
  type?: string
  voice_provider?: string
  sms_provider?: string
  toll_free?: string
  local_presence?: string
  number_type?: string
  feature?: string
  deleted?: string | boolean
}

export interface PhoneNumberCreatePayload {
  [key: string]: unknown
}

export interface PhoneNumberUpdatePayload {
  [key: string]: unknown
}

export interface BulkUpdateStatusPayload {
  phone_number_ids: number[]
  status_id: number
}

export interface BulkUpdateStatusResult {
  success_count: number
  [key: string]: unknown
}

export interface SelectOption {
  id: number
  name: string
  number?: string
  uuid?: string
}

export function createPhoneNumbersResource(transport: HttpTransport) {
  return {
    list: (params?: PhoneNumberListParams) => {
      const qs = stringifyParams((params ?? {}) as Record<string, unknown>)
      return transport.get<PaginatedApiResult<PhoneNumber>>(
        qs ? `/app/v1/phone-numbers/?${qs}` : '/app/v1/phone-numbers/',
      )
    },

    /** Get also works with legacy /phone-numbers/ path */
    listLegacy: (params?: {
      pbx_id?: number
      offset?: number
      limit?: number
      search?: string
    }) => {
      const qs = stringifyParams((params ?? {}) as Record<string, unknown>)
      return transport.get<PaginatedApiResult<PhoneNumber>>(
        qs ? `/phone-numbers/?${qs}` : '/phone-numbers/',
      )
    },

    getPhoneNumber: (
      id: number,
      options?: { includeDiagnostics?: boolean },
    ) => {
      const qs = stringifyParams({
        deleted: true,
        include_diagnostics: options?.includeDiagnostics || undefined,
      })
      return transport.get<PhoneNumber>(`/app/v1/phone-numbers/${id}/?${qs}`)
    },

    createPhoneNumber: (payload: PhoneNumberCreatePayload) =>
      transport.post<PhoneNumber>('/app/v1/phone-numbers/', payload),

    updatePhoneNumber: (id: number, payload: PhoneNumberUpdatePayload) =>
      transport.patch<PhoneNumber>(`/app/v1/phone-numbers/${id}/`, payload),

    deletePhoneNumber: (id: number) =>
      transport.delete<void>(`/app/v1/phone-numbers/${id}/`),

    restore: (id: number) =>
      transport.post<PhoneNumber>(`/app/v1/phone-numbers/${id}/restore/`),

    bulkUpdateStatus: (payload: BulkUpdateStatusPayload) =>
      transport.post<BulkUpdateStatusResult>(
        '/app/v1/phone-numbers/bulk-update-status/',
        payload,
      ),

    /** Trigger a no-op save (forces reprocessing without changes) */
    noopSave: (id: number) =>
      transport.put<PhoneNumber>(`/app/v1/phone-numbers/${id}/noop-save/`),

    /** Get available phone number statuses */
    getStatuses: () =>
      transport.get<SelectOption[]>('/app/v1/phone-number-statuses/'),

    /** Get available providers */
    getProviders: () => transport.get<SelectOption[]>('/app/v1/providers/'),

    /** Get extensions for phone number assignment (filtered) */
    getExtensions: (pbxId: number) =>
      transport.get<unknown>(
        `/app/v1/extensions/select/?pbx_id=${pbxId}&limit=1000`,
      ),

    /** Get virtual fax machines for a PBX */
    getFaxMachines: (pbxId: number) =>
      transport.get<unknown>(
        `/app/v1/pbxes/${pbxId}/virtual-fax-machines-select/`,
      ),

    /** Get integrations for a PBX */
    getIntegrations: (pbxId: number) =>
      transport.get<unknown>(`/app/v1/pbxes/${pbxId}/integrations-select/`),
  }
}
